import React from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

/**
 * KPI tile: mono micro-label, tabular number, optional delta.
 *
 *   <HudStat label="Hot leads" value={42} tone="gold" icon={Flame} />
 *   <HudStat label="Bonded" value="$1.2M" delta={8} deltaSuffix="%" deltaLabel="vs last month" />
 *
 * Props
 *   label        string — the micro-label
 *   value        number | string — numbers get toLocaleString()
 *   tone         'accent' (default) | 'gold' | 'success' | 'danger' | 'neutral'
 *   icon         lucide icon component, drawn in the tone colour
 *   delta        number — signed change; arrow and colour follow the sign
 *   deltaSuffix  string appended to the delta ('%', ' pts')
 *   deltaLabel   string after the delta ('vs last week')
 *   hint         node — dim footnote when there is no delta
 *   onClick      makes the whole tile a button
 *   title        native tooltip
 */
export default function HudStat({
  label, value, tone = 'accent', icon: Icon, delta, deltaSuffix = '', deltaLabel, hint, onClick, title,
}) {
  const shown = typeof value === 'number' ? value.toLocaleString() : (value ?? '—');
  const hasDelta = typeof delta === 'number' && !Number.isNaN(delta);
  const dir = !hasDelta ? null : delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  const DeltaIcon = dir === 'up' ? ArrowUpRight : dir === 'down' ? ArrowDownRight : Minus;
  const deltaText = hasDelta ? `${delta > 0 ? '+' : ''}${delta.toLocaleString()}${deltaSuffix}` : null;

  const Tag = onClick ? 'button' : 'div';
  const classes = ['hud-stat', tone && tone !== 'accent' && `tone-${tone}`].filter(Boolean).join(' ');

  return (
    <Tag className={classes} onClick={onClick} title={title} type={onClick ? 'button' : undefined}>
      <span className="hud-stat-head">
        <span className="hud-label">{label}</span>
        {Icon && <Icon size={15} strokeWidth={2} aria-hidden="true" />}
      </span>
      <span className="hud-stat-value">{shown}</span>
      {(hasDelta || hint) && (
        <span className="hud-stat-foot">
          {hasDelta && (
            <span className={`hud-stat-delta ${dir}`}>
              <DeltaIcon size={12} strokeWidth={2.5} aria-hidden="true" />
              <span className="sr-only">{dir === 'up' ? 'Up' : dir === 'down' ? 'Down' : 'No change'} </span>
              {deltaText}
            </span>
          )}
          {hasDelta && deltaLabel && <span>{deltaLabel}</span>}
          {!hasDelta && hint}
        </span>
      )}
    </Tag>
  );
}
